import { ApiError, ValidationApiError } from "@anna-money/anna-web-lib/api";
import type { CompanyFormValues } from "helpers/forms/abn/companyTypes";
import type { FlowTypeFormValues } from "helpers/forms/acn/flowTypes";
import type { DirectorStepFormValues } from "helpers/forms/au/directorTypes";
import type { IdentiyFormValues } from "helpers/forms/au/identityTypes";
import type { ConsentFormValues } from "helpers/forms/consentTypes";
import { extractBackendValidationError } from "helpers/forms/errors";
import { getUrlParam } from "helpers/url";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import type { BusinessActivitiesStore } from "services/businessActivities/businessActivitiesStore";
import { type CompanyStore } from "services/company/companyStore";
import type { CompanyBusinessActivitiesStore } from "services/companyBusinessActivities/au/companyBusinessActivitiesStore";
import type { ConsentStore } from "services/consent/consentStore";
import type { CountryStore } from "services/country/countryStore";
import { type DirectorEntity, type DirectorStore } from "services/director/directorStore";
import { type IncorporationStore } from "services/incorporation/incorporationStore";
import { type IssuesStore } from "services/issues/issuesStore";
import { type PaymentMethodsStore } from "services/paymentMethods/paymentMethodsStore";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { RegisterFormStoreBase } from "services/registerForm/registerFormStoreBase";
import { CompanyAbnStepEntity, type CompanyAbnStepStore } from "services/stepStore/au/abn/companyAbnStepStore";
import { IdentityStepEntity, type IdentityStepStore } from "services/stepStore/au/identityStepStore";
import type { SumsubApplicationStore } from "services/sumsub/sumsubApplicationStore";
import { flowTypeUrlParam, parseFlowTypeUrlParam } from "services/user/flowTypeUrlParam";
import type { UserStore } from "services/user/userStore";
import { FlowType } from "services/user/userTypes";
import { FormError } from "types/errors";
import { type FormSubmitResult, FormValidationError } from "types/form";
import { type AbnRegisterFormStepsStore } from "./abnRegisterFormStepsStore";
import { AbnFormStep } from "./constants";

export class AbnRegisterFormStore extends RegisterFormStoreBase<AbnFormStep> {
  private _pendingPricingPlan: PricingPlan | null | undefined;

  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _paymentMethodsStore: PaymentMethodsStore,
    private readonly _consentStore: ConsentStore,
    private readonly _companyBusinessActivitiesStore: CompanyBusinessActivitiesStore,
    private readonly _businessActivitiesStore: BusinessActivitiesStore,
    protected readonly _userStore: UserStore,
    protected readonly _sumsubApplicationStore: SumsubApplicationStore,
    protected readonly _analyticsManager: AnalyticsManager,
    protected readonly _registerFormStepsStore: AbnRegisterFormStepsStore,
    protected readonly _incorporationStore: IncorporationStore,
    protected readonly _companyIssuesStore: IssuesStore,
    protected readonly _companyAbnStepStore: CompanyAbnStepStore,
    protected readonly _identityStepStore: IdentityStepStore,
    protected readonly _countryStore: CountryStore,
  ) {
    super(_registerFormStepsStore, _userStore, _incorporationStore, _companyIssuesStore, _analyticsManager);
  }

  protected async _init(): Promise<void> {
    await this._companyStore.init();

    await Promise.all([
      this._consentStore.init(),
      this._directorStore.init(),
      this._sumsubApplicationStore.init(),
      this._companyIssuesStore.init(),
      this._incorporationStore.init(),
      this._paymentMethodsStore.init(),
      this._companyBusinessActivitiesStore.init(),
      this._businessActivitiesStore.init(),
      this._countryStore.init(),
    ]);

    await this._tryApplyFlowTypeFromUrl();
  }

  protected _getRelevantStep(): AbnFormStep {
    // Check if flow type is selected
    if (!this._userStore.flowType) {
      return AbnFormStep.FLOW_SELECT;
    }
    // Check consent
    if (!this._consentStore.hasAuConsents) {
      return AbnFormStep.CONSENT;
    }
    if (!this._companyStore.checkAbnCompanyDetailExists()) {
      return AbnFormStep.COMPANY;
    }

    if (!this._directorStore.checkAllDirectorDetailExists()) {
      return AbnFormStep.DIRECTOR;
    }

    if (this._sumsubApplicationStore.sumsubApplication) {
      return AbnFormStep.IDENTITY;
    }

    return AbnFormStep.CONSENT;
  }

  protected async _updateStepData(data: unknown, entity?: unknown): Promise<FormSubmitResult> {
    try {
      switch (this._registerFormStepsStore.activeStep.type) {
        case AbnFormStep.FLOW_SELECT: {
          const { flowType = FlowType.AcnAbn } = data as FlowTypeFormValues;
          await this._applyFlowSelection(flowType);
          break;
        }
        case AbnFormStep.CONSENT:
          await this._consentStore.updateConsents(data as ConsentFormValues);
          break;
        case AbnFormStep.COMPANY:
          await this._companyAbnStepStore.updateEntity(data as CompanyFormValues, entity as CompanyAbnStepEntity);
          if (this._pendingPricingPlan !== undefined && this._companyStore.company) {
            await this._companyStore.updatePricingPlan(this._pendingPricingPlan);
            this._pendingPricingPlan = undefined;
          }
          break;
        case AbnFormStep.DIRECTOR: {
          await this._directorStore.updateMainDirector(data as DirectorStepFormValues, entity as DirectorEntity);
          break;
        }
        case AbnFormStep.IDENTITY:
          await this._identityStepStore.updateEntity(data as IdentiyFormValues, entity as IdentityStepEntity);
          break;
        case AbnFormStep.CONFIRM: {
          await this._createIncorporation();
          break;
        }
      }
    } catch (error) {
      if (error instanceof ValidationApiError) {
        return extractBackendValidationError(error);
      }
      if (error instanceof ApiError || error instanceof FormError) {
        return new FormValidationError(error.message);
      }
      throw error;
    }
  }

  private async _applyFlowSelection(flowType: FlowType): Promise<void> {
    await this._userStore.updateCurrentFlow(flowType);
    await this._updatePricingPlanFromFlowType(flowType);
  }

  private async _tryApplyFlowTypeFromUrl(): Promise<void> {
    const flowType = parseFlowTypeUrlParam(getUrlParam(flowTypeUrlParam));

    if (!flowType || this._userStore.flowType) {
      return;
    }

    await this._applyFlowSelection(flowType);

    this._analyticsManager.event("flow-type-applied-from-url", { flowType });
  }

  private async _updatePricingPlanFromFlowType(flowType: FlowType): Promise<void> {
    let pricingPlan: PricingPlan;

    switch (flowType) {
      case FlowType.SoleTraderAbn:
        pricingPlan = PricingPlan.SoleTraderLaunch;
        break;
      case FlowType.Abn:
      case FlowType.AcnAbn:
        pricingPlan = PricingPlan.EasyCompany;
        break;
      default:
        this._analyticsManager.event("flow-type-pricing-plan-mapping-error", {
          flowType,
          reason: "unknown_flow_type",
        });
        throw new Error(`Unknown flow type for pricing plan mapping: ${String(flowType)}`);
    }

    if (!this._companyStore.company) {
      // Company record does not exist yet (new user hasn't reached the Company step).
      // Defer the pricing-plan update until the company is created in the COMPANY step.
      this._pendingPricingPlan = pricingPlan;
      return;
    }

    try {
      await this._companyStore.updatePricingPlan(pricingPlan);

      this._analyticsManager.event("pricing-plan-updated-from-flow-selection", {
        flowType,
        pricingPlan,
      });
    } catch (error) {
      this._analyticsManager.event("pricing-plan-update-error", {
        flowType,
        pricingPlan,
        error: (error as Error).message,
      });
      throw error;
    }
  }
}
